/**
 * PayoutsTab — admin queue for valet payout requests.
 *
 * Loads from the Node backend's /api/admin/payouts endpoint. Default view
 * is pending (status='requested'); admin can flip a row to 'sent' after
 * sending the funds via the chosen method (Zelle / Venmo / Cash App).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Button,
    CircularProgress,
    ToggleButtonGroup,
    ToggleButton,
    Chip,
    IconButton,
    Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { styled } from '@mui/system';
import { getPayouts, markPayoutPaid } from '../../services/adminService';
import { VALET, FONTS, TYPE } from '../../theme';

const FILTERS = [
    { key: 'requested', label: 'Pending' },
    { key: 'sent', label: 'Paid' },
    { key: 'all', label: 'All' },
];

const SectionEyebrow = styled(Typography)({
    ...TYPE.eyebrow,
    marginBottom: '6px',
});

const SectionTitle = styled(Typography)({
    ...TYPE.sectionTitle,
    marginBottom: 0,
});

const SummaryCard = styled(Box)({
    backgroundColor: VALET.green,
    color: '#ffffff',
    borderRadius: '14px',
    padding: '22px 24px',
    marginBottom: '24px',
    boxShadow: VALET.shadow,
    '@media (max-width: 768px)': {
        padding: '18px 16px',
    },
});

const TableWrapper = styled(Box)({
    backgroundColor: VALET.surface,
    border: `1px solid ${VALET.border}`,
    borderRadius: '14px',
    overflow: 'hidden',
    boxShadow: VALET.shadow,
});

const StyledHeaderCell = styled(TableCell)({
    fontFamily: FONTS.mono,
    fontSize: '10px',
    color: VALET.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.16em',
    fontWeight: 500,
    padding: '14px 16px',
    '@media (max-width: 768px)': { padding: '10px 12px' },
    borderBottom: `1px solid ${VALET.border}`,
    backgroundColor: VALET.surfaceMuted,
});

const StyledCell = styled(TableCell)({
    padding: '14px 16px',
    '@media (max-width: 768px)': { padding: '10px 12px' },
    fontSize: '13px',
    fontFamily: FONTS.sans,
    color: VALET.textPrimary,
    borderBottom: `1px solid ${VALET.border}`,
    verticalAlign: 'middle',
});

const FilterToggle = styled(ToggleButton)({
    padding: '6px 14px',
    borderRadius: '999px !important',
    fontSize: '12px',
    fontFamily: FONTS.sans,
    fontWeight: 500,
    textTransform: 'none',
    border: `1px solid ${VALET.border} !important`,
    color: VALET.textSecondary,
    backgroundColor: VALET.bg,
    '&:hover': {
        backgroundColor: VALET.greenLight,
        color: VALET.green,
    },
    '&.Mui-selected': {
        backgroundColor: VALET.green,
        color: '#ffffff',
        border: `1px solid ${VALET.green} !important`,
        '&:hover': {
            backgroundColor: VALET.greenDark,
        },
    },
});

const HandleMono = styled('span')({
    fontFamily: FONTS.mono,
    fontSize: '12px',
    backgroundColor: VALET.surfaceMuted,
    color: VALET.textPrimary,
    padding: '4px 8px',
    borderRadius: 6,
    border: `1px solid ${VALET.border}`,
});

const formatDateTime = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const formatUSD = (cents) =>
    `$${((Number(cents) || 0) / 100).toFixed(2)}`;

const statusChipProps = (status) => {
    const base = {
        fontSize: 10,
        height: 22,
        fontFamily: FONTS.mono,
        fontWeight: 600,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        borderRadius: '999px',
        border: '1px solid',
    };
    if (status === 'sent') {
        return {
            label: 'Paid',
            sx: { ...base, backgroundColor: VALET.successBg, color: VALET.success, borderColor: `${VALET.success}33` },
        };
    }
    if (status === 'cancelled') {
        return {
            label: 'Cancelled',
            sx: { ...base, backgroundColor: VALET.dangerBg, color: VALET.danger, borderColor: `${VALET.danger}33` },
        };
    }
    return {
        label: 'Pending',
        sx: { ...base, backgroundColor: VALET.bronzeMuted, color: VALET.bronze, borderColor: VALET.bronzeHairline },
    };
};

export default function PayoutsTab() {
    const [statusFilter, setStatusFilter] = useState('requested');
    const [payouts, setPayouts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [markingId, setMarkingId] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await getPayouts(statusFilter);
            setPayouts(res?.success ? res.payouts || [] : []);
        } catch (err) {
            setError(err.message || 'Failed to load payouts');
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleMarkPaid = async (payout) => {
        const ok = window.confirm(
            `Mark this payout as PAID?\n\n` +
                `${payout.valetName} — ${formatUSD(payout.amount)} via ${
                    payout.methodLabel
                } (${payout.handleDisplay})\n\n` +
                `This will tell the valet that funds were sent. Do this only AFTER you actually send the money.`
        );
        if (!ok) return;
        try {
            setMarkingId(payout._id);
            await markPayoutPaid(payout._id);
            await fetchData();
        } catch (err) {
            alert(err.message || 'Failed to mark as paid');
        } finally {
            setMarkingId(null);
        }
    };

    const handleCopy = (text) => {
        if (!text) return;
        try {
            navigator.clipboard.writeText(text);
        } catch {
            // ignore — older browsers
        }
    };

    const totalPending = payouts.filter((p) => p.status === 'requested').length;
    const totalPendingAmount = payouts
        .filter((p) => p.status === 'requested')
        .reduce((sum, p) => sum + (p.amount || 0), 0);

    return (
        <Box>
            <SectionEyebrow>Payouts</SectionEyebrow>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: '20px', gap: '16px', flexWrap: 'wrap' }}>
                <SectionTitle>Outgoing to valets</SectionTitle>
                <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <ToggleButtonGroup
                        value={statusFilter}
                        exclusive
                        onChange={(_, v) => v && setStatusFilter(v)}
                        size='small'
                        sx={{ gap: '6px' }}>
                        {FILTERS.map((f) => (
                            <FilterToggle key={f.key} value={f.key}>
                                {f.label}
                            </FilterToggle>
                        ))}
                    </ToggleButtonGroup>
                    <Tooltip title='Refresh'>
                        <IconButton
                            size='small'
                            onClick={fetchData}
                            disabled={loading}
                            sx={{ color: VALET.textSecondary }}>
                            <RefreshIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {statusFilter === 'requested' && totalPending > 0 && (
                <SummaryCard>
                    <Typography sx={{ ...TYPE.eyebrow, color: 'rgba(255,255,255,0.7)' }}>
                        Awaiting action
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '14px', mt: '8px', flexWrap: 'wrap' }}>
                        <Typography sx={{ fontFamily: FONTS.display, fontSize: '38px', fontWeight: 500, letterSpacing: '-0.03em', color: '#ffffff', lineHeight: 1 }}>
                            {formatUSD(totalPendingAmount)}
                        </Typography>
                        <Typography sx={{ fontFamily: FONTS.sans, fontSize: '14px', color: 'rgba(255,255,255,0.85)' }}>
                            across {totalPending} {totalPending === 1 ? 'payout' : 'payouts'}
                        </Typography>
                    </Box>
                </SummaryCard>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                    <CircularProgress sx={{ color: VALET.green }} size={28} />
                </Box>
            ) : error ? (
                <Typography sx={{ color: VALET.danger, backgroundColor: VALET.dangerBg, fontSize: 13, p: '14px 18px', borderRadius: '8px', fontFamily: FONTS.sans }}>
                    {error}
                </Typography>
            ) : payouts.length === 0 ? (
                <TableWrapper>
                    <Box sx={{ p: '48px 20px', textAlign: 'center', color: VALET.textMuted, fontSize: 14, fontFamily: FONTS.sans }}>
                        {statusFilter === 'requested'
                            ? 'No pending payouts. You\u2019re caught up.'
                            : 'No records.'}
                    </Box>
                </TableWrapper>
            ) : (
                <TableWrapper>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <StyledHeaderCell>Valet</StyledHeaderCell>
                                <StyledHeaderCell>Phone</StyledHeaderCell>
                                <StyledHeaderCell>Method</StyledHeaderCell>
                                <StyledHeaderCell>Handle</StyledHeaderCell>
                                <StyledHeaderCell align='right'>
                                    Amount
                                </StyledHeaderCell>
                                <StyledHeaderCell>Requested</StyledHeaderCell>
                                <StyledHeaderCell>Status</StyledHeaderCell>
                                <StyledHeaderCell align='right'>
                                    Action
                                </StyledHeaderCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {payouts.map((p) => {
                                const isMarking = markingId === p._id;
                                const isPending = p.status === 'requested';
                                return (
                                    <TableRow key={p._id} sx={{
                                        '&:hover td': { backgroundColor: VALET.surfaceHover },
                                        '&:last-child td': { borderBottom: 'none' },
                                    }}>
                                        <StyledCell sx={{ fontWeight: 600 }}>{p.valetName}</StyledCell>
                                        <StyledCell sx={{ color: VALET.textSecondary }}>{p.valetPhone || '—'}</StyledCell>
                                        <StyledCell sx={{ color: VALET.textSecondary }}>{p.methodLabel}</StyledCell>
                                        <StyledCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <HandleMono>{p.handleDisplay}</HandleMono>
                                                <Tooltip title='Copy handle'>
                                                    <IconButton
                                                        size='small'
                                                        onClick={() => handleCopy(p.handleDisplay)}
                                                        sx={{ color: VALET.textMuted }}>
                                                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </StyledCell>
                                        <StyledCell
                                            align='right'
                                            sx={{
                                                fontFamily: FONTS.mono,
                                                fontSize: '14px',
                                                fontWeight: 600,
                                                color: VALET.ink,
                                            }}>
                                            {formatUSD(p.amount)}
                                        </StyledCell>
                                        <StyledCell sx={{ fontFamily: FONTS.mono, fontSize: '12px', color: VALET.textMuted }}>
                                            {formatDateTime(p.createdAt)}
                                        </StyledCell>
                                        <StyledCell>
                                            <Chip size='small' {...statusChipProps(p.status)} />
                                            {p.status === 'sent' && p.sentAt && (
                                                <Typography sx={{ fontSize: 10, fontFamily: FONTS.mono, color: VALET.textMuted, mt: '4px' }}>
                                                    {formatDateTime(p.sentAt)}
                                                </Typography>
                                            )}
                                        </StyledCell>
                                        <StyledCell align='right'>
                                            {isPending ? (
                                                <Button
                                                    size='small'
                                                    variant='contained'
                                                    onClick={() => handleMarkPaid(p)}
                                                    disabled={isMarking}
                                                    sx={{
                                                        backgroundColor: VALET.green,
                                                        textTransform: 'none',
                                                        fontFamily: FONTS.sans,
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                        borderRadius: '8px',
                                                        padding: '6px 14px',
                                                        boxShadow: 'none',
                                                        '&:hover': {
                                                            backgroundColor: VALET.greenDark,
                                                            boxShadow: VALET.shadow,
                                                        },
                                                    }}>
                                                    {isMarking ? 'Saving…' : 'Mark paid'}
                                                </Button>
                                            ) : (
                                                <Typography sx={{ fontSize: 12, color: VALET.textDim }}>—</Typography>
                                            )}
                                        </StyledCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableWrapper>
            )}
        </Box>
    );
}
